import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAiProduct } from '@store/ai-product/selectors';
import classNames from 'classnames';
import { actions } from '@store/companies';
import { ROUTES } from '@app/routes/routesUrls';
import useNavigate from '@shared/router/useNavigate';
import Button from '@shared/components/button';
import CheckIcon from '@resources/icons/CheckIcon';
import { renderAiProduct } from '@store/ai-product/actions';
import DownloadIcon from '@shared/icons/DownloadIcon';
import LimitBanner from '@shared/components/page-parts/LimitBanner';

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    fontSize: '14px',
    padding: '16px',
    width: '100%',
    height: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '16px',
    color: '#334D6E',
    fontWeight: 700,
    textDecoration: 'underline',
    '& > div': {
      width: '100%',
      maxWidth: '200px',
    },
    marginBottom: '16px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '20px',
  },
  contentHeader: {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '20px',
    fontWeight: 700,
  },
  contentSubHeader: {
    color: '#9CACC2',
    fontSize: '16px',
    fontWeight: 400,
  },
  imagesWrapper: {
    display: 'flex',
    gap: '20px',
    maxWidth: '100%',
    overflowX: 'auto',
    padding: '20px',
    border: '1px solid #00C98F',
    borderRadius: '4px',
  },
  uploadImage: {
    width: '268px',
    height: '184px',
    objectFit: 'cover',
    borderRadius: '4px',
    border: '1px solid #E3E9F1',
    flex: '0 0 268px',
    position: 'relative',
    '& > img': {
      width: '100%',
      height: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
  },
  siloImage: {
    '& > img': {
      objectFit: 'contain',
    },
  },
  imageEditorWrapper: {
    display: 'flex',
    marginTop: '20px',
    gap: '16px',
    flex: '1',
  },
  productImageEditorWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flex: '1 1 auto',
    maxWidth: '95%',
  },
  submitButton: {
    alignSelf: 'center',
  },
  imageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    overflow: 'auto',
  },
  actions: {
    display: 'flex',
    marginTop: '50px',
    gap: '16px',
    '& > button': {
      minWidth: '142px',
      width: '100%',
    },
    '& > button:first-child': {
      borderColor: '#192A3E',
      color: '#192A3E',
    },
  },
  chooseImageEmpty: {
    objectFit: 'cover',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '268px',
    height: '184px',
    border: '1px solid #E3E9F1',
    '& > img': {
      width: '100%',
      height: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
  },
  uploadVideo: {
    width: '268px',
    position: 'relative',
    display: 'flex',
    height: '184px',
    '& > img': {
      width: '100%',
      maxWidth: '268px',
      maxHeight: '184px',
      height: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
    '& > video': {
      width: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
  },
  iconButton: {
    cursor: 'pointer',
    display: 'flex',
    border: `1px solid ${theme.palette.primary.main}`,
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.palette.primary.main,
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    padding: '4px',
    '& > svg path': {
      color: '#fff',
      fill: '#fff',
    },
  },
  reviewWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  reviewHeader: {
    fontSize: '16px',
    fontWeight: 700,
  },
  contentWrapper: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: '100%',
    marginBottom: '15px',
    flexDirection: 'row',
    gap: '20px',
  },
  downloadTitle: {
    fontSize: '16px',
    fontWeight: 400,
    color: '#112943',
  },
  listDownloadWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  listDownload: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  listItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    fontWeight: 400,
  },
  btnDownload: {
    maxWidth: '153px',
    width: '100%',
    maxHeight: '44px',
    height: '100%',
    '& > span': {
      display: 'flex',
      flexDirection: 'row',
      gap: '10px',
      '& > svg path': {
        color: '#fff',
        fill: '#fff',
      },
    },
  },
}));

export function DownloadStep() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.companies.settings);
  const user = useSelector((state) => state.app.user);
  const { goTo } = useNavigate();
  const product = useSelector(selectAiProduct);
  const { getCurrentSettings } = actions;

  useEffect(() => {
    dispatch(getCurrentSettings(user.company.id));
  }, [dispatch, user, getCurrentSettings]);

  const isAiProductReadOnly = useMemo(() => {
    return !(settings?.studioConfig?.ai_p_active ?? true);
  }, [settings]);

  const handleCreatePack = () => {
    dispatch(renderAiProduct({
      name: `AI Product ${Date.now().toString(36)}${Math.random().toString(36).substring(2)}`,
      images: [],
      aspect: {
        name: 'Original',
        x: 0,
        y: 0,
      },
    }));
    goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.NEW}`);
  };

  const handleDownload = async () => {
    try {
      const downloadUrl = `${process.env.REACT_APP_SERVER_URL}/ai-products/${product.id}/download`;

      const response = await fetch(downloadUrl, { method: 'GET' });
      if (!response.ok) {
        throw new Error(`Failed to download. Status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ai-product-pack-${product.id}.zip`);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };


  const siloSrcCount = product.images.filter((image) => image.siloSrc).length;
  const selectedLifestyleCount = product.images.reduce(
    (count, image) => count + (image.lifestyle?.filter((item) => item.selected).length || 0),
    0,
  );
  const videoCount = product.images.reduce(
    (count, image) => count + (image.lifestyle?.filter((item) => item.isVideoUsed && item.videoSrc).length || 0),
    0,
  );
  const textCount = product.texts?.filter((text) => text.text && text.text.trim().length > 0 && text.selected).length || 0;

  return (
    <div className={ classes.pageWrapper }>
      <div className={ classes.header }>
        Product Detail Page Content Pack
      </div>
      <LimitBanner show={ isAiProductReadOnly } />
      <div className={ classes.content }>
        <div className={ classes.contentHeader }>
            Congratulations!
          <div className={ classes.contentSubHeader }>
            Your pack is ready! You can download it.
          </div>
        </div>
        <div className={ classes.listDownloadWrapper }>
          <div className={ classes.downloadTitle }>
            In this package, the following items were created:
          </div>
          <div className={ classes.listDownload }>
          {siloSrcCount > 0 && (
              <div className={ classes.listItem }>
                <div className={ classes.iconButton }>
                  <CheckIcon size="small" />
                </div>
                {`${siloSrcCount} x silo-shots`}
              </div>
          )}
            {selectedLifestyleCount > 0 && (
              <div className={ classes.listItem }>
                <div className={ classes.iconButton }>
                  <CheckIcon size="small" />
                </div>
                {`${selectedLifestyleCount} x lifestyle images`}
              </div>
            )}
            {videoCount > 0 && (
              <div className={ classes.listItem }>
                <div className={ classes.iconButton }>
                  <CheckIcon size="small" />
                </div>
                {`${videoCount} x product video`}
              </div>
            )}
            {textCount > 0 && (
              <div className={ classes.listItem }>
                <div className={ classes.iconButton }>
                  <CheckIcon size="small" />
                </div>
                {`${textCount} x product description`}
              </div>
            )}
          </div>
          <Button
            variant="outlined"
            className={ classes.btnDownload }
            onClick={ handleDownload }
          >
            <DownloadIcon />
            Download
          </Button>
        </div>
        </div>
        <div className={ classes.actions }>
          <Button
            color="secondary"
            variant="outlined"
            onClick={ () => {
              goTo(`${ROUTES.AI_PRODUCT.ROOT}`);
            } }
          >
            Go to Main
          </Button>
          {!isAiProductReadOnly && (
          <Button
            variant="outlined"
            className={ classes.btnCreate }
            onClick={ handleCreatePack }
          >
            Create new pack
          </Button>
          )}
        </div>
      </div>
  );
}
